import { Link, NavLink as RouterLink } from 'react-router-dom';

import PropTypes from 'prop-types';

import { Breadcrumbs as MuiBreadcrumbs } from '@mui/material';
import Icon from '@mui/material/Icon';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import { useIntl } from 'react-intl';
import { isNumber } from 'utils/stringUtils';
import { useMemo } from 'react';
import { containsVariable, NOT_FOUND } from 'utils/formatMessage';
import { Helmet } from 'react-helmet-async';

function Breadcrumbs({ routePath, view, icon, light, pageTitle }) {
  const { formatMessage: msg } = useIntl();

  // Removes the first element (portal) and the "view" element
  let effectiveRoutePath = routePath.slice(1);
  if (effectiveRoutePath.length <= 1) {
    // static view
  } else {
    // dynamic view: first token defines the view type / second token defines the view name / other tokes defines view details
    effectiveRoutePath = effectiveRoutePath.filter((item, index) => {
      if (index === 1 && item === view) {
        return false;
      }
      return !isNumber(item) && !isMD5(item);
    });
  }
  const currentPageName = effectiveRoutePath.slice(-1);

  const title = useMemo(() => {
    let title = pageTitle;
    if (!title) {
      title = msg({
        id: `view.${view}.title`,
        defaultMessage: NOT_FOUND
      });
      if (title === NOT_FOUND || containsVariable(title)) {
        title = msg({ id: `page.${currentPageName}` });
      }
    }
    return title;
  }, [currentPageName, msg, pageTitle, view]);

  return (
    <MDBox mr={1}>
      <MuiBreadcrumbs
        sx={{
          '& .MuiBreadcrumbs-separator': {
            color: ({ palette: { white, grey } }) => (light ? white.main : grey[600])
          }
        }}
      >
        <Link to="/home" aria-label={msg({ id: `page.home` })}>
          <MDTypography
            component="span"
            variant="body2"
            color={light ? 'white' : 'dark'}
            opacity={0.8}
            sx={{ lineHeight: 0 }}
          >
            <Icon>{icon}</Icon>
          </MDTypography>
        </Link>
        {effectiveRoutePath.slice(0, -1).map((el, index) => (
          <Link
            component={RouterLink}
            aria-label={msg({ id: `page.${el}` })}
            to={`${el}/${view}`}
            key={el}
            state={{
              view
            }}
          >
            <MDTypography
              component="span"
              variant="button"
              fontWeight="regular"
              textTransform="capitalize"
              color={light ? 'white' : 'dark'}
              opacity={0.8}
              sx={{ lineHeight: 0 }}
            >
              {index === 0
                ? msg({ id: `view.${view}.shortTitle`, defaultMessage: msg({ id: `page.${el}` }) })
                : msg({ id: `page.${el}` })}
            </MDTypography>
          </Link>
        ))}
        <MDTypography
          variant="button"
          fontWeight="regular"
          textTransform="capitalize"
          color={light ? 'white' : 'dark'}
          sx={{ lineHeight: 0 }}
        >
          {effectiveRoutePath.length === 1
            ? msg({
                id: `view.${view}.shortTitle`,
                defaultMessage: msg({ id: `page.${currentPageName}` })
              })
            : msg({
                id: `view.${view}.${effectiveRoutePath.slice(1).join('.')}.shortTitle`,
                defaultMessage: msg({ id: `page.${currentPageName}` })
              })}
        </MDTypography>
      </MuiBreadcrumbs>
      <MDTypography
        fontWeight="bold"
        textTransform="capitalize"
        variant="h6"
        color={light ? 'white' : 'dark'}
        noWrap
      >
        {title}
      </MDTypography>
      <Helmet>
        <title>
          {title} - {msg({ id: 'product.name' })}
        </title>
      </Helmet>
    </MDBox>
  );
}

function isMD5(hash) {
  const md5Regex = /^[0-9a-fA-F]{32}$/;
  return md5Regex.test(hash);
}

// Setting default values for the props of Breadcrumbs
Breadcrumbs.defaultProps = {
  light: false
};

// Typechecking props for the Breadcrumbs
Breadcrumbs.propTypes = {
  routePath: PropTypes.oneOfType([PropTypes.string, PropTypes.array]).isRequired,
  view: PropTypes.string,
  icon: PropTypes.node.isRequired,
  light: PropTypes.bool,
  pageTitle: PropTypes.string
};

export default Breadcrumbs;
